import { hasAdvertisingConsent, runAfterMeasurementConsent } from "./consent"
import type { CommerceItem, DataLayerEvent, PixelEventData } from "./types"
import { generateEventId, withoutUndefined } from "./utils"

const DEFAULT_STORYWORTH_ITEM = {
  image_url: "https://static.storyworth.com/images/opengraph/default1.jpg",
  item_category: "Memoir",
  item_id: "storyworth",
  item_name: "Storyworth",
  item_variant: "default",
}

const dispatchedDataLayerPixels = new Set<string>()

/**
 * Every key a `storyworth.pixel` push can carry. GTM merges each push into a
 * persistent data model, so a key omitted from a later push would still read
 * as the previous pixel's value in a Data Layer Variable. Pushing an explicit
 * `null` for every absent key keeps each event self-contained.
 */
const PIXEL_EVENT_KEYS = [
  "sw_event_name",
  "sw_event_id",
  "sw_event_source_url",
  "sw_value",
  "sw_currency",
  "sw_transaction_id",
  "sw_items",
  "sw_new_user_first_purchase",
  "sw_coupon",
  "sw_customer_status",
  "sw_quantity",
  "sw_user_email_sha1",
  "sw_user_email_sha256",
  "sw_user_external_id_sha256",
  "sw_user_first_name_sha256",
  "sw_user_last_name_sha256",
  "event_id",
  "order_id",
  "value",
  "transaction_id",
  "items",
  "ecommerce",
] as const

export function pushDataLayerEvent(event: DataLayerEvent) {
  const payload = withoutUndefined(event)
  if (window.storyworthLogAnalyticsEvents) {
    console.debug("[GA event]", payload.event, payload)
  }
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

export function pushDataLayerEventAfterConsent(event: DataLayerEvent) {
  runAfterMeasurementConsent(() => pushDataLayerEvent(event))
}

/**
 * Pushes one `storyworth.pixel` event, at most once per `pixel:event_id`.
 * Callers assign `event_id` (see `ensureEventId`) before dispatching.
 */
export function dispatchDataLayerPixel(pixel: string, data: PixelEventData) {
  const eventKey = `${pixel}:${data.event_id}`
  if (dispatchedDataLayerPixels.has(eventKey)) {
    return
  }

  pushDataLayerEvent(pixelDataLayerEvent(pixel, data))
  dispatchedDataLayerPixels.add(eventKey)
}

/**
 * Assigns an event id to pixel data that has none. Mutates `data` so the
 * data-layer push and the CAPI request for the same pixel share one id.
 */
export function ensureEventId(data: PixelEventData) {
  data.event_id ??= generateEventId()
  return data.event_id
}

export function resetDataLayerPixelsForTests() {
  dispatchedDataLayerPixels.clear()
}

function pixelDataLayerEvent(pixel: string, data: PixelEventData) {
  const currency = data.currency?.toUpperCase()
  const items = commerceItems(pixel, data)
  const itemQuantity = quantity(items)
  const orderId = data.order?.id
  const event = withoutUndefined({
    event: "storyworth.pixel",
    sw_event_name: pixel,
    sw_event_id: data.event_id,
    sw_event_source_url: data.event_source_url,
    sw_value: data.value,
    sw_currency: currency,
    sw_transaction_id: orderId,
    sw_items: items,
    sw_new_user_first_purchase: data.new_user_first_purchase || undefined,
    sw_coupon: data.coupon,
    sw_customer_status: data.customer_status,
    sw_quantity: itemQuantity,
    ...storyworthUserDataEventFields(data),
    event_id: data.event_id,
    order_id: orderId,
    value: data.value,
    transaction_id: orderId,
    items,
    ecommerce:
      pixel === "purchase"
        ? withoutUndefined({
            transaction_id: orderId,
            value: data.value,
            currency,
            items,
          })
        : undefined,
  })

  return {
    ...Object.fromEntries(PIXEL_EVENT_KEYS.map((key) => [key, null])),
    ...event,
  }
}

function commerceItems(pixel: string, data: PixelEventData) {
  if (data.items?.length) {
    return data.items
  }

  const orderCommerceItems = orderItems(data)
  if (orderCommerceItems?.length) {
    return orderCommerceItems
  }

  if (
    ![
      "view_item_list",
      "select_item",
      "add_to_cart",
      "begin_checkout",
      "purchase",
    ].includes(pixel)
  ) {
    return undefined
  }

  if (data.value === undefined) {
    return undefined
  }

  return [
    withoutUndefined({
      ...DEFAULT_STORYWORTH_ITEM,
      price: data.value,
      quantity: 1,
    }),
  ]
}

function orderItems(data: PixelEventData) {
  return data.order?.items?.map((item) =>
    withoutUndefined({
      item_id: item.parent,
      item_name: item.parent,
      price: item.amount === undefined ? undefined : item.amount / 100,
      quantity: item.quantity,
    }),
  )
}

function quantity(items?: CommerceItem[]) {
  if (!items?.length) {
    return undefined
  }

  return items.reduce((total, item) => total + (item.quantity || 0), 0)
}

function storyworthUserDataEventFields(data: PixelEventData) {
  if (!hasAdvertisingConsent()) return {}

  const userData = window.storyworthUserData

  return withoutUndefined({
    sw_user_email_sha1: userData?.email_sha1 ?? data.email_sha1,
    sw_user_email_sha256: userData?.email_sha256 ?? data.email_sha256,
    sw_user_external_id_sha256:
      userData?.external_id_sha256 ?? data.external_id_sha256,
    sw_user_first_name_sha256:
      userData?.first_name_sha256 ?? data.first_name_sha256,
    sw_user_last_name_sha256:
      userData?.last_name_sha256 ?? data.last_name_sha256,
  })
}
